import Vue from 'vue';
import Vuex from 'vuex';
import isTokenValid from '@/utils/token';
import config from '@/utils/config';

Vue.use(Vuex);

const STATE_VAL_JSON = 'STATE_VAL_JSON';
const STATE_VAL_STRING = 'STATE_VAL_STRING';

const setStateFactory = (prop, isJson = STATE_VAL_STRING, fn) => (state, payload) => {
  state[prop] = payload;
  localStorage.setItem(prop, isJson === STATE_VAL_JSON ? JSON.stringify(payload) : payload);

  if (fn) { fn(state, payload); }
};

const storeSignOut = () => {
  localStorage.removeItem('jwtacc');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem('dealer');

  delete Vue.prototype.$http.defaults.headers.jwtacc;
};

const getStorageCart = () => {
  const storage = JSON.parse(localStorage.getItem('cart')) || [];
  if (Array.isArray(storage)) {
    return storage;
  }

  localStorage.setItem('cart', '[]');
  return [];
};

export default new Vuex.Store({
  state: {
    workMode: config.workMode,
    user: JSON.parse(localStorage.getItem('user')),
    dealer: JSON.parse(localStorage.getItem('dealer')) || {},
    cart: getStorageCart(),
    realm: localStorage.getItem('realm') || '',
    sidebarIsOpen: true,
    authLocation: JSON.parse(localStorage.getItem('auth-location')) || null,
    orderOptions: {
      page: 1,
      populate: 'createdBy',
      query: {},
      sort: '-date'
    }
  },

  mutations: {
    setOrdersQuery(state, filters){
      state.orderOptions.query = filters.reduce((acc, { key, value, type }) => {
        if (!value) {
          return acc;
        }
        if (type === 'number') {
          acc[key] = +value;
          return acc;
        }
        if (key === 'date'){
          acc[key] = value
        } else {
          acc[key] = {
            $regex: `.*${value}.*`,
            $options: 'i',
          };
        }
        if (key === 'status' && value === 'notPaid') {
          acc[key] = { $ne: 'Closed' };
        }

        return acc;
      }, {});
    },
      
    setOrdersOptions(state, payload){
      const obj = Object.assign({}, this.orderOptions, payload);
      if (payload.itemsPerPage){
        obj.pageSize = payload.itemsPerPage;
      }
      if (payload.page){
        obj.page = payload.page
      }
      if (payload.sortBy && payload.sortBy.length) {
        obj.sort = `${payload.sortDesc[0] ? '-' : ''}${payload.sortBy[0]}`;
      }
      state.orderOptions = obj;
    },
    setRealm: setStateFactory('realm', STATE_VAL_STRING),

    setUser: setStateFactory('user', STATE_VAL_JSON),

    setDealer: setStateFactory('dealer', STATE_VAL_JSON),

    signOut: (state) => {
      state.user = null;
      state.dealer = null;

      storeSignOut();
    },

    setAuthRedirectLocation(state, payload) {
      state.authLocation = payload;
      localStorage.setItem('auth-location', JSON.stringify(payload));
    },

    clearAuthRedirectLocation(state) {
      state.authLocation = null;
      localStorage.removeItem('auth-location');
    },

    setToken: (state, jwt, refreshToken) => {
      localStorage.setItem('jwtacc', jwt);
      Vue.prototype.$http.defaults.headers.jwtacc = jwt;

      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }
    },

    refreshCart(state) {
      state.cart = getStorageCart();
    },

    addToCart(state, position) {
      state.cart = getStorageCart();
      const positionIndex = state.cart.findIndex((item) => item.code === position.code);
      if (positionIndex === -1) {
        state.cart.push(position);
      } else {
        state.cart[positionIndex] = position;
      }
      localStorage.setItem('cart', JSON.stringify(state.cart));
    },

    changePositionQuantity(state, { code, quantity }) {
      state.cart = getStorageCart();
      const position = state.cart.find((item) => item.code === code);
      if (position) {
        position.quantity = quantity;
      }
      localStorage.setItem('cart', JSON.stringify(state.cart));
    },

    incrementPositionQuantity(state, { code }) {
      state.cart = getStorageCart();
      const position = state.cart.find((item) => item.code === code);
      if (position) {
        position.quantity += 1;
      }
      localStorage.setItem('cart', JSON.stringify(state.cart));
    },

    decrementPositionQuantity(state, { code }) {
      state.cart = getStorageCart();
      const position = state.cart.find((item) => item.code === code);
      if (position && position.quantity > 1) {
        position.quantity -= 1;
      }
      localStorage.setItem('cart', JSON.stringify(state.cart));
    },

    deletePosition(state, code) {
      state.cart = getStorageCart();
      const positionIndex = state.cart.findIndex((item) => item.code === code);
      if (positionIndex !== -1) {
        state.cart.splice(positionIndex, 1);
      }
      localStorage.setItem('cart', JSON.stringify(state.cart));
    },

    replaceCart(state, positions = []) {
      state.cart = positions;
      localStorage.setItem('cart', JSON.stringify(state.cart));
    },

    toggleSidebar(state) {
      state.sidebarIsOpen = !state.sidebarIsOpen;
    },

    openSidebar(state) {
      state.sidebarIsOpen = true;
    },
  },

  getters: {
    full: (state) => state.user && state.user.interfaces.includes('Full'),
    business: (state) => state.user && state.user.interfaces.includes('Business'),
    supplier: (state) => state.user && state.user.interfaces.includes('Supplier'),
    dealer: (state) => state.user && state.user.interfaces.includes('Dealer'),
    approver: (state) => state.user && state.user.interfaces.includes('Dealer approver'),
    security: (state) => state.user && state.user.interfaces.includes('Security'),
    editor: (state) => state.user && state.user.interfaces.includes('Editor'),
    read: (state) => state.user && state.user.interfaces.includes('Read'),
    user: (state) => state.user,
    authLocation: (state) => state.authLocation,
    orderParams: (state) => {
      const options = Object.assign({}, state.orderOptions);
      if (options.query['date']){
        const value = options.query['date'];
        options.query['$expr'] = {
          $function: {
            body: `function (createDate, value) {
              const timeRegex = new RegExp(/.*${value}.*/i);
              return timeRegex.test(createDate);
            }`,
            args: [{ $dateToString: { date: "$date" } }, value],
            lang: "js"
          }
        }
              
        delete options.query['date']
      }
      return options
    }
  },

  actions: {
    ssoSignOut: async ({ commit, state }) => {
      const { $keycloak, $log } = Vue.prototype;
      $log.debug('SSO Sign out');

      commit('signOut');

      // Keycloak Logout will redirect user, so it should be last in action
      if (state.realm
        && $keycloak
        && $keycloak.adapters[state.realm]
        && $keycloak.adapters[state.realm].realm) {
        await $keycloak.adapters[state.realm].logout();
      }
    },

    getBackendToken: async ({ commit }, payload) => {
      const { $http, $log } = Vue.prototype;
      try {
        // We will keep jwtacc in defaults, but try refresh token with empty jwtacc header
        const { data } = await $http.$token.post('main/token-validate', {
          token: payload,
        });

        const { jwtacc, refreshToken } = data;
        return commit('setToken', jwtacc, refreshToken);
      } catch (error) {
        $log.debug('Error Get backend token', error);
        return Promise.reject(payload);
      }
    },

    refreshBackendToken: ({ dispatch }, payload) => {
      const currentRefreshToken = localStorage.getItem('refreshToken');
      if (!currentRefreshToken || !isTokenValid(currentRefreshToken)) {
        return Promise.reject(payload);
      }

      return dispatch('getBackendToken', currentRefreshToken);
    },

    refreshKeycloakToken: async ({ commit, state }, payload) => {
      const { $keycloak, $log } = Vue.prototype;

      const adapter = $keycloak.adapters[state.realm];
      try {
        await adapter.updateToken();
        return commit('setToken', adapter.token);
      } catch (error) {
        $log.debug('Error refresh keycloak token', error);
        return Promise.reject(payload);
      }
    },

    performAuth: async ({ commit }, payload) => {
      const {
        jwtacc, refreshToken, locale, app,
      } = payload;

      const {
        $http, $log, $sentry,
      } = app;

      $log.debug('performAuth');
      const reqHeaders = { headers: { jwtacc } };

      try {
        const { data: userData } = await $http.$token.get('main/getself', reqHeaders);
        $log.debug('userData', userData);
        
        try{
          const params = { populate: 'creditLimit' };
          const { data: dealerData } = await $http.$token.get(`dealers/${userData.asdb}`, { ...reqHeaders, params });
          commit('setDealer', dealerData);
        } catch (e){
          void e
        }
        
        try{
          if (locale && locale !== userData.language) {
            await $http.$token.put(`users/${userData.id}`, { language: locale }, reqHeaders);
  
            userData.language = locale;
          }
        } catch (e){
          void e
        }
        

        commit('setUser', userData);
        commit('setToken', jwtacc);

        if ($sentry) {
          $sentry.configureScope((scope) => {
            scope.setTag('dealer', userData.asdb);
            scope.setUser({
              username: userData.login, userId: userData.id, code: userData.asdb, wslId: userData.wslId,
            });
          });
        }

        if (refreshToken) {
          localStorage.setItem('refreshToken', refreshToken);
        }
      } catch (e) {
        $log.debug('getSelfError', e.message);

        const { data = {}, type } = e.response || {};

        $log.debug('Perform auth handle-error', type, data, e.response);
        throw e;
      }
    },
  },
});
